/**
 * <PaperBanner>: hand-drawn paper banner primitive.
 *
 * Per IMPLEMENTATION_PLAN §13.11 (Design H4, M3.T13.5).
 *
 * A reusable notification banner with a hand-drawn paper rectangle and
 * a slight rotation (±1.5° based on `kind`). Used by:
 *   - Spectator-rejoin: "You'll join next round."
 *   - Drawer-disconnect grace: "Drawer reconnecting..." (M4)
 *   - Reload-required: "Server updated, please reload."
 *   - Room-gone: "This room no longer exists."
 *   - "You're in!" promotion banner (PROMOTE_SPECTATORS transition)
 *
 * Behaviors:
 *   - `info` kind: slight positive rotation (+1°), auto-dismiss after `duration`.
 *   - `warn` kind: neutral rotation (0°), auto-dismiss.
 *   - `error` kind: slight negative rotation (−1.5°), persistent (no auto-dismiss).
 *   - `duration` defaults to 3000ms for info/warn; errors ignore `duration`.
 *
 * The SVG border gives the hand-drawn paper look. The background is `--paper`
 * with a slight shadow. No animation dependencies, CSS handles the fade.
 */

import { useEffect, useState } from "preact/hooks";

export type PaperBannerKind = "info" | "warn" | "error";

type PaperBannerProps = {
  kind: PaperBannerKind;
  message: string;
  /** Auto-dismiss duration in ms. Defaults to 3000 for info/warn; ignored for error. */
  duration?: number;
  /** Called when the banner is dismissed. */
  onDismiss?: () => void;
};

/** Rotation in degrees per kind. */
const ROTATION: Record<PaperBannerKind, number> = {
  info: 1,
  warn: 0,
  error: -1.5,
};

export function PaperBanner({ kind, message, duration = 3000, onDismiss }: PaperBannerProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (kind === "error") return; // errors are persistent
    const t = setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, duration);
    return () => clearTimeout(t);
  }, [kind, duration, onDismiss]);

  if (!visible) return null;

  const rotation = ROTATION[kind];
  const isError = kind === "error";

  return (
    <div
      class={["paper-banner", `paper-banner--${kind}`].join(" ")}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      style={{ transform: `rotate(${rotation}deg)` }}
      data-testid="paper-banner"
    >
      {/* Hand-drawn SVG border */}
      <svg
        class="paper-banner-border"
        viewBox="0 0 320 52"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        preserveAspectRatio="none"
      >
        <path
          d={
            // Wobbly rounded rectangle
            "M 8 3 C 30 2 100 1 160 2 C 220 1 290 2 314 3 " +
            "C 318 4 319 8 319 14 C 319 28 319 36 318 44 " +
            "C 317 48 314 50 310 50 C 280 51 160 51 20 50 " +
            "C 8 50 2 48 2 44 C 1 36 1 20 2 10 C 2 5 5 3 8 3 Z"
          }
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span class="paper-banner-text">{message}</span>
      {isError && (
        <button
          class="paper-banner-dismiss"
          onClick={() => {
            setVisible(false);
            onDismiss?.();
          }}
          aria-label="Dismiss"
          type="button"
        >
          ✕
        </button>
      )}
    </div>
  );
}
